<template>
   <div class='confirm-plan'>
      <div class="heading">
         <check-mark color="primary" size="x-large"/>
         <div class='title'>Plan Overview</div>
         <plan-description background="transparent"/>
         <div class='inputs'>
            <span>
               <input type="checkbox" id="plan-completed" v-model="is_completed" >
               <label for="plan-completed"> Mark plan as completed</label>
            </span>
            <atom-button size="small" @click="markComplete">Save</atom-button>
         </div>
      </div>
      <div class="body">
         <div class='question'>
            <span>Report Generation</span>
            <div class="btn-group">
               <atom-button @click="generateReport" :disabled="isGeneratingReport">
                  {{ isGeneratingReport ? '...generating' : reportGenerated ? 'Re-generate report' : 'Generate Report' }}
               </atom-button>
               <atom-button v-if="reportGenerated"><a :href="downloadLink">Download Report</a></atom-button>
            </div>
         </div>
         <div class='question'>
            <span>Map Generation</span>
            <div class="btn-group">
               <atom-button @click="generateMaps" :disabled="isGeneratingMaps">
                  {{ isGeneratingMaps ? '...generating' : mapsGenerated ? 'Re-generate maps' : 'Generate Maps' }}
               </atom-button>
               <atom-button v-if="mapsGenerated"><a :href="mapDownloadLink">Download Maps</a></atom-button>
            </div>
         </div>
         <div class='btn-group'>
            <atom-button><router-link :to="{ name: 'sa' }">Back</router-link></atom-button>
            <atom-button><router-link :to="{ name: 'home' }">Go to home</router-link></atom-button>
         </div>
      </div>
   </div>
</template>

<script>
import { fetchWithAuthToken, postWithAuthToken } from '../../../../../axios';
import CheckMark from '../../../../atoms/Login/CheckMark';
import PlanDescription from '../PlanDescription';
import AtomButton from '../../../../atoms/Login/Button';
import { confirm, showSuccess,showError } from '../../../../../js/ui-helpers';
import { mapActions, mapGetters } from 'vuex';

export default {
   name: 'final-page',
   components: {
      AtomButton,
      PlanDescription,
      CheckMark,
   },
   data()
   {
      return {
         goals: [],
         downloadLink: null,
         isGeneratingReport: false,
         isGeneratingMaps: false,
         mapDownloadLink: null,
      };
   },
   computed: {
      ...mapGetters('plan', ['getPlan']),
      is_completed: {
         get()
         {
            return this.getPlan && this.getPlan.is_completed;
         },
         set(newValue, oldValue)
         {
            if (newValue !== oldValue) this.setPlanCompleted(newValue);
         }
      },
      planId() {
         return this.$route.params.id;
      },
      reportGenerated()
      {
         // if this has downloadLink then it's safe to assume that the report is generated
         // otherwise the downloadLink will be set to null
         return Boolean(this.downloadLink);
      },
      mapsGenerated()
      {
         return Boolean(this.mapDownloadLink)
      },
      generateReportUrl()
      {
         return `api/plan/${this.planId}/report-generate/`;
      },
      generateMapUrl() {
         return `api/plan/${this.planId}/map-generate/`;
      }
   },
   async created()
   {
      try {
         const {data} = await fetchWithAuthToken(this.generateReportUrl);
         this.downloadLink = data.download_link;
      }
      catch (e)
      {
         console.error(e)
      }
      try {
         const {data} = await fetchWithAuthToken(this.generateMapUrl);
         this.mapDownloadLink = data.download_link;
      }
      catch (e)
      {
         console.error(e)
      }
   },
   methods: {
      ...mapActions('plan', ['setPlanCompleted']),
      markComplete()
      {
         const msg = this.is_completed ?
            "Are you sure? You won't be able to make changes once you've cofirmed plan's completion."
            :
            "Are you sure you want to edit the plan? You've already confirmed plan's completion";
         confirm({
            title: 'Confirm plan completion',
            msg, 
            onSuccess: () =>
            {
               postWithAuthToken(`api/plan/${this.planId}/plan-completion/`, {is_completed: this.is_completed})
               .then(() => showSuccess('Plan Completion', "Successfully confirmed plan completion"))
               .catch(() => showError('Plan Completion', "Error saving plan"));
            }
         });
      },
      async generateReport()
      {
         try
         {
            this.isGeneratingReport = true;
            const {data} = await postWithAuthToken(this.generateReportUrl)
            this.downloadLink = data.download_link;
         }
         catch (e)
         {
            showError('Couldn\'t generate report.')
         }
         finally 
         {
            this.isGeneratingReport = false;
         }
      },
      async generateMaps()
      {
         try
         {
            this.isGeneratingMaps = true;
            const {data} = await postWithAuthToken(this.generateMapUrl)
            this.mapDownloadLink = data.download_link;
         }
         catch (e)
         {
            showError('Couldn\'t generate report.')
         }
         finally 
         {
            this.isGeneratingMaps = false;
         }
      },

   }
}
</script>

<style lang="scss" scoped>
.confirm-plan {
   margin: 0.5em;
   color: $font-color;
   font-family: $primary-font;
   border-radius: 0.3em;

   > .heading {
      padding: 0.6em;
      background: $secondary-bg;
      border-radius: 0.3em;
      text-align: center;
      > .title {
         text-align: center;
         font-size: 1.8em;
      }
      .inputs {
         display: flex;
         flex-direction: column;
         align-items: center;
      }
   }
   .goals {
      padding: 0.5em;
   }
   .body {
      margin-top: 0.5em;
      padding: 0.5em;
      background: $secondary-bg;
      border-radius: 0.3em;
      .body-title {
         font-weight: 600;
         padding: 0.2em 0.5em;
         font-size: 1.1em;
      }
   }
   .goal {
      background: $page-bg;
      padding: 0.5em;
      margin-bottom: 1em;
      border-radius: 5px;
      .goal-title {
         padding: 0.3em 0.2em;
         font-weight: 600;
         margin-bottom: 0.3em;
      }
      .objective {
         padding: 0.2em;
         &:not(:last-child) {
            margin-bottom: .7em;
         }
         border-radius: 5px;
         background: white;
         .objective-head {
            .objective-title {
               font-weight: 600;
               padding: 0.5em 0.2em;
            }
            display: flex;
            justify-content: space-between;
         }
         .activity {
            padding: 0.4em 1em;
         }
      }
   }
   .question {
      text-align: center;
      span {
         font-weight: 600;
      }
   }
   .btn-group {
      display: flex;
      justify-content: center;
      > * {
         margin-right: 0.3em;
         a {
            color:inherit;
         }
         &:last-child {
            margin-right: 0;
         }
      }
   }
}
</style>
